import "../SettingsPage.css";
import "./EditProfile.css";
import React, { useEffect, useRef, useState } from "react";
import { AppDispatch } from "../../../redux/store";
import { useDispatch, useSelector } from "react-redux";
import RootState from "../../../redux/states/root.state";
import Header from "../../../components/common/Header/Header";
import { updateUser } from "../../../redux/actions/user.actions";
import StatusModal from "../../../components/common/Modals/StatusModal";
import AddressModal from "../../../components/common/Modals/AddressModal";
import { fetchAllDealerships, fetchDealership } from "../../../redux/actions/dealership.actions";
import { CheckCircleIcon, ChevronLeftIcon, ChevronRightIcon, HomeIcon } from "../../../components/common/Icons/Icons";

const EditProfile = () => {
    const dispatch: AppDispatch = useDispatch();
    const {data:user, loading:userLoading} = useSelector((state: RootState) => state.user);
    const {data:dealership, loading: dealershipLoading} = useSelector((state: RootState) => state.dealership);
    const [submitted, setSubmitted] = useState(false);
    const [addressOpen, setAddressOpen] = useState(false);

    // Feedback popup. Success redirects back to settings; error keeps the user here to retry.
    const [isOpen, setOpen] = useState(false);
    const [modalContent, setContent] = useState("");
    const [modalStatus, setModalStatus] = useState<"success" | "error">("success");

    // Field-level validation errors surfaced as helper text (e.g. "email already taken").
    const [nameError, setNameError] = useState("");
    const [emailError, setEmailError] = useState("");
    const [phoneError, setPhoneError] = useState("");

    const [updateInfo, setUpdateInfo] = useState<any>({});
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    // Starts as the masked number from the API; only sent once the user changes it
    const [phone, setPhone] = useState('');
    const [lastFetchedDealershipId, setLastFetchedDealershipId] = useState<string | null>(null);

    // Dealerships selection state
    const { data: dealerships, loading: dealershipsLoading, meta: dealershipsMeta } = useSelector((state: RootState) => state.dealerships);
    const [dealershipInput, setDealershipInput] = useState("");
    const [selectedDealershipId, setSelectedDealershipId] = useState("");
    const [dealershipPage, setDealershipPage] = useState(1);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    // Fetch dealerships when input or page changes
    useEffect(() => {
        if (dropdownOpen) {
        const query = `name=${encodeURIComponent(dealershipInput)}&page=${dealershipPage}`;
        dispatch(fetchAllDealerships(query));
        }
    }, [dealershipInput, dealershipPage, dropdownOpen, dispatch]);

    // Close dropdown on outside click
      useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
          if (
            inputRef.current &&
            !inputRef.current.contains(event.target as Node) &&
            !(event.target as HTMLElement).closest('.dealership-dropdown')
          ) {
            setDropdownOpen(false);
          }
        }
        if (dropdownOpen) {
          document.addEventListener('mousedown', handleClickOutside);
        } else {
          document.removeEventListener('mousedown', handleClickOutside);
        }
        return () => {
          document.removeEventListener('mousedown', handleClickOutside);
        };
      }, [dropdownOpen]);

    useEffect(() => {
        if (user) {
            setName(user?.name);
            setEmail(user?.email);
            setPhone(user?.phone ?? '');
            setSelectedDealershipId(user?.dealership_id);

            if ( user?.dealership_id && user?.dealership_id !== lastFetchedDealershipId) {
                dispatch(fetchDealership(user.dealership_id));
                setLastFetchedDealershipId(user.dealership_id);
            }
        }

      }, [user, dispatch, lastFetchedDealershipId]);

    useEffect(() => {
        if (dealership) {
            setDealershipInput(dealership?.name);
        }
      }, [dealership]);

    // Normalise a single field error (the API returns either a string or a string[]).
    const fieldMessage = (val: any): string => (Array.isArray(val) ? val.filter(Boolean).join(" ") : String(val));

    const clearErrors = () => {
        setNameError("");
        setEmailError("");
        setPhoneError("");
    };

    const handleSubmit = async () => {
        setSubmitted(true);
        clearErrors();

        const result = await dispatch(updateUser({...updateInfo, uuid:user?.id}));

        if (updateUser.fulfilled.match(result)) {
            setUpdateInfo(({phone: _, ...rest}: any) => rest);
            setModalStatus("success");
            setContent("Your profile has been updated successfully.");
            setOpen(true);
            return;
        }

        const payload: any = result.payload;

        // Field-level validation errors → inline helper text under the matching field.
        if (payload && typeof payload === "object" && !Array.isArray(payload)) {
            const leftovers: string[] = [];
            Object.entries(payload).forEach(([field, val]) => {
                const message = fieldMessage(val);
                if (field === "email") setEmailError(message);
                else if (field === "name") setNameError(message);
                else if (field === "phone") setPhoneError(message);
                else leftovers.push(message); // e.g. username, which isn't editable here
            });

            // Anything without a matching field still gets surfaced in the error popup.
            if (leftovers.length) {
                setModalStatus("error");
                setContent(leftovers.join(" "));
                setOpen(true);
            }
            return;
        }

        // General / non-field error → error popup, stay on the page.
        setModalStatus("error");
        setContent(typeof payload === "string" ? payload : "An error occurred while updating your profile. Please try again later.");
        setOpen(true);
    }
    


    const dealershipOptions = (dealerships || []).filter((d: any) => d && d.id && d.name);
    const hasNextPage = Boolean(dealershipsMeta && dealershipPage < dealershipsMeta.total_pages);

    return (
        <div className="page st-page">
            <Header withLogo={false} withNavBtn={false} withUserAvatar={false}/>

            <main className="st-main">
                <div className="st-heading">
                    <h1 className="page-heading">Edit profile</h1>
                    <p className="st-subheading">Your contact details and preferred dealership.</p>
                </div>

            {
                (userLoading && !submitted) || dealershipLoading ?
            <div className="st-loading"><span className="spinner" role="progressbar" aria-label="Loading"/></div>
            :
            <form className="st-form" onSubmit={(e) => { e.preventDefault(); if (name && email && selectedDealershipId && !userLoading) handleSubmit(); }}>
                <section className="st-group" aria-labelledby="ep-details">
                    <h2 className="st-group-title" id="ep-details">Personal details</h2>
                    <div className="card st-card-body">
                        <div className="field">
                            <label className="field-label" htmlFor="ep-username">Username</label>
                            <input id="ep-username" className="input" defaultValue={user?.username} disabled/>
                        </div>

                        <div className="field">
                            <label className="field-label" htmlFor="ep-name">Name<span className="st-required" aria-hidden="true">*</span></label>
                            <input
                                id="ep-name"
                                className="input"
                                required
                                value={name}
                                onChange={(e)=>{setName(e.target.value);setNameError("");setUpdateInfo({...updateInfo,name:e.target.value})}}
                                disabled={!name}
                                aria-invalid={!!nameError}
                                aria-describedby="ep-name-msg"
                                autoComplete="name"
                            />
                            {(nameError || !name) &&
                                <p id="ep-name-msg" className={nameError ? 'st-field-error' : 'st-field-hint'}>{nameError || 'Name is required.'}</p>}
                        </div>

                        <div className="field">
                            <label className="field-label" htmlFor="ep-email">Email<span className="st-required" aria-hidden="true">*</span></label>
                            <input
                                id="ep-email"
                                className="input"
                                type="email"
                                required
                                value={email}
                                onChange={(e)=>{setEmail(e.target.value);setEmailError("");setUpdateInfo({...updateInfo,email:e.target.value})}}
                                disabled={!email}
                                aria-invalid={!!emailError}
                                aria-describedby="ep-email-msg"
                                autoComplete="email"
                            />
                            {(emailError || !email) &&
                                <p id="ep-email-msg" className={emailError ? 'st-field-error' : 'st-field-hint'}>{emailError || 'Email is required.'}</p>}
                        </div>

                        <div className="field">
                            <label className="field-label" htmlFor="ep-phone">Phone</label>
                            <input
                                id="ep-phone"
                                className="input"
                                type="tel"
                                value={phone}
                                // Select the masked number so typing replaces it rather than editing around the dots
                                onFocus={(e)=>{ if (phone && phone === user?.phone) e.target.select(); }}
                                onChange={(e)=>{
                                    const value = e.target.value;
                                    setPhone(value);
                                    setPhoneError("");
                                    if (value === (user?.phone ?? '')) {
                                        const {phone: _, ...rest} = updateInfo;
                                        setUpdateInfo(rest);
                                    } else {
                                        // Blank removes the number
                                        setUpdateInfo({...updateInfo, phone: value.trim() === '' ? null : value});
                                    }
                                }}
                                aria-invalid={!!phoneError}
                                aria-describedby="ep-phone-msg"
                                autoComplete="tel"
                            />
                            {(phoneError || phone) &&
                                <p id="ep-phone-msg" className={phoneError ? 'st-field-error' : 'st-field-hint'}>{phoneError || 'Clear the field to remove your number.'}</p>}
                        </div>
                    </div>
                </section>

                <section className="st-group" aria-labelledby="ep-address">
                    <h2 className="st-group-title" id="ep-address">Address</h2>
                    <ul className="card st-list">
                        <li>
                            <button type="button" className="st-row" onClick={()=>setAddressOpen(true)}>
                                <span className="st-row-icon"><HomeIcon/></span>
                                <span className="st-row-label">Edit address</span>
                                <span className="st-row-end"><ChevronRightIcon/></span>
                            </button>
                        </li>
                    </ul>
                </section>

                <section className="st-group" aria-labelledby="ep-dealer">
                    <h2 className="st-group-title" id="ep-dealer">Preferred dealership</h2>
                    <div className="card st-card-body">
                        <div className="field ep-dealer">
                            <label className="field-label" htmlFor="ep-dealer-input">Dealership<span className="st-required" aria-hidden="true">*</span></label>
                            <input
                                id="ep-dealer-input"
                                ref={inputRef}
                                className="input"
                                type="search"
                                placeholder="Search dealerships"
                                required
                                value={dealershipInput}
                                onFocus={() => setDropdownOpen(true)}
                                onChange={e => {
                                    const value = e.target.value;
                                    setDealershipInput(value);
                                    setDealershipPage(1);

                                    // If user clears the input, clear the selected dealership and close dropdown
                                    if (value === "") {
                                    setSelectedDealershipId("");
                                    setDropdownOpen(false);
                                    } else {
                                    setDropdownOpen(true);
                                    }
                                }}
                                role="combobox"
                                aria-expanded={dropdownOpen}
                                aria-controls="ep-dealer-list"
                                autoComplete="off"
                            />

                            {dropdownOpen && (
                            <div className="dealership-dropdown ep-dealer-dropdown">
                                {dealershipsLoading ? (
                                <div className="ep-dealer-empty">Loading…</div>
                                ) : (
                                <>
                                    {dealershipOptions.length === 0 ? (
                                    <div className="ep-dealer-empty">No dealerships found</div>
                                    ) : (
                                    <ul className="ep-dealer-list" id="ep-dealer-list" role="listbox">
                                        {dealershipOptions.map((d: any) => (
                                        <li key={d.id} role="option" aria-selected={selectedDealershipId === d.id}>
                                            <button
                                                type="button"
                                                className={`ep-dealer-option ${selectedDealershipId === d.id ? 'selected' : ''}`}
                                                onClick={() => {
                                                    setDealershipInput(d.name);
                                                    setSelectedDealershipId(d.id);
                                                    setUpdateInfo({...updateInfo, dealership_id:d.id})
                                                    setDropdownOpen(false);
                                                }}
                                            >
                                                {d.name}
                                                {selectedDealershipId === d.id && <CheckCircleIcon/>}
                                            </button>
                                        </li>
                                        ))}
                                    </ul>
                                    )}
                                    <div className="ep-dealer-pager">
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-ghost"
                                            disabled={dealershipPage <= 1}
                                            onClick={() => dealershipPage > 1 && setDealershipPage(dealershipPage - 1)}
                                        >
                                            <ChevronLeftIcon/> Previous
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-ghost"
                                            disabled={!hasNextPage}
                                            onClick={() => hasNextPage && setDealershipPage(dealershipPage + 1)}
                                        >
                                            Next <ChevronRightIcon/>
                                        </button>
                                    </div>
                                </>
                                )}
                            </div>
                            )}
                        </div>
                    </div>
                </section>

                <div className="st-actions">
                    <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={!name || !email || !selectedDealershipId || userLoading}>
                        {userLoading ? 'Saving…' : 'Save changes'}
                    </button>
                </div>
            </form>
            }
            </main>

            <AddressModal open={addressOpen} setOpen={setAddressOpen}/>

            <StatusModal
                isOpen={isOpen}
                setOpen={setOpen}
                status={modalStatus}
                content={modalContent}
            />
        </div>
    );
};

export default EditProfile;
